import type {
  ImpactColumnExperience,
  ImpactColumnExperienceType,
} from "./columnExperience";
// Default images are bundled local assets; no external image URLs are initialized.
import aolNepalImage1 from "../assets/AOL_Nepal_1.jpeg";
import aolNepalImage2 from "../assets/AOL_Nepal_2.jpeg";
import pmReliefImage1 from "../assets/PM_Fund_Nepal_1.jpeg";
import pmReliefImage2 from "../assets/PM_Fund_Nepal_2.jpeg";

export type AnchorCubeTone = "coral" | "blue" | "green" | "purple" | "gold";

export type ImpactBookSectionKey =
  | "poster"
  | "overview"
  | "funding"
  | "recipientPm"
  | "recipientAol"
  | "contribute"
  | "contributeStatus"
  | "thanks"
  | "adminContributors"
  | "people"
  | "updates";

export const CREATOR_ONLY_SECTIONS = new Set<string>([
  "people",
  "adminContributors",
]);

/** Collectors commit funds here. Creators, partners, and invited partners do not. */
export const COLLECTOR_ONLY_SECTIONS = new Set<string>([
  "contribute",
  "contributeStatus",
]);

export type AnchorCubeColumn = {
  id: string;
  /** API id of this subpage, when persisted via POST /main-card/pages. */
  pageId?: number;
  title: string;
  description?: string;
  icon?: string;
  /** Maps onto a synthetic Impact Card book section when set. */
  sectionKey?: ImpactBookSectionKey;
  experienceType?: ImpactColumnExperienceType;
  experience?: ImpactColumnExperience;
  /** Review state after the card creator verifies or rejects partner-uploaded content. */
  verified?: boolean;
  reviewStatus?: "pending" | "Verified" | "Rejected";
  creator?: string;
};

export const CUBE_SECTION_BY_ID: Record<string, NonNullable<AnchorCubeColumn["sectionKey"]>> = {
  "cube-impact-card": "poster",
  "cube-overview": "poster",
  "cube-funding": "funding",
  "cube-pm-relief": "recipientPm",
  "cube-aol": "recipientAol",
  "cube-contribute": "contribute",
  "cube-contribute-status": "contributeStatus",
  "cube-thanks": "thanks",
  "cube-updates": "thanks",
  "cube-contributors": "adminContributors",
  "cube-community": "people",
};

export function cubeSectionKey(cube: AnchorCube): AnchorCubeColumn["sectionKey"] | undefined {
  return CUBE_SECTION_BY_ID[cube.id];
}

export type AnchorCube = {
  id: string;
  /** API id of this cube page, when persisted via POST /main-card/pages. */
  pageId?: number;
  title: string;
  description?: string;
  icon?: string;
  thumbnailUrl?: string;
  experienceType?: ImpactColumnExperienceType;
  experience?: ImpactColumnExperience;
  tone: AnchorCubeTone;
  columns: AnchorCubeColumn[];
  collapsed?: boolean;
  locked?: boolean;
  sectionKey?: AnchorCubeColumn["sectionKey"];
  creator?: string;
  verified?: boolean;
  reviewStatus?: "pending" | "Verified" | "Rejected";
};

export function isPartnerImpactCube(cube: AnchorCube) {
  const section = cubeSectionKey(cube);
  if (section === "thanks" || cube.id === "cube-thanks" || cube.id === "cube-updates") return true;
  return !cube.locked && cube.creator === "PARTNER";
}

export const ANCHOR_CUBE_TONES: AnchorCubeTone[] = [
  "coral",
  "blue",
  "green",
  "purple",
  "gold",
];


export const DEFAULT_IMPACT_ANCHOR_CUBES: AnchorCube[] = [
  {
    id: "cube-impact-card",
    title: "임팩트 카드 소개",
    description: "함께 만드는 변화와 나눔의 이야기",
    icon: "target",
    tone: "coral",
    locked: true,
    collapsed: true,
    columns: [
      {
        id: "news-nepal-flood-1",
        title: "네팔 홍수 피해 현황",
        description:
          "네팔의 홍수 피해 규모와 피해 지역 주민들의 상황을 전하는 뉴스 영상입니다.",
        icon: "youtube",
        experienceType: "Youtube",
        experience: {
          title: "네팔 홍수 관련 뉴스",
          description:
            "네팔의 홍수 피해 상황을 전하는 언론 보도입니다.",
          url: "https://youtu.be/vMqMIWddBCM",
        },
      },
      {
        id: "news-nepal-flood-2",
        title: "홍수 피해 지역 주민들의 이야기",
        description:
          "이번 캠페인의 배경이 된 피해 주민들의 상황과 긴급한 지원 필요성을 살펴봅니다.",
        icon: "youtube",
        experienceType: "Youtube",
        experience: {
          title: "홍수 피해 지역 주민들의 상황",
          description:
            "재난으로 어려움을 겪고 있는 지역사회의 상황을 전하는 뉴스 영상입니다.",
          url: "https://youtu.be/XqhRiAuiQ7c",
        },
      },
      {
        id: "news-nepal-flood-3",
        title: "긴급 구호와 복구를 향한 여정",
        description:
          "긴급 구호 활동부터 피해 지역의 장기적인 복구까지, 앞으로 필요한 지원을 살펴봅니다.",
        icon: "youtube",
        experienceType: "Youtube",
        experience: {
          title: "네팔의 구호 및 복구 활동",
          description:
            "재난 대응과 복구 과정을 이해하기 위한 뉴스 영상입니다.",
          url: "https://youtu.be/2WWxEbAdDl0",
        },
      },
    ],
  },

  {
    id: "cube-funding",
    title: "모금 목표",
    description: "모금 목표와 기부금 사용 계획",
    icon: "give",
    tone: "green",
    locked: true,
    collapsed: true,
    columns: [],
  },

  {
    id: "cube-pm-relief",
    title: "네팔 총리 구호 기금",
    description: "캠페인 기금 지원 기관",
    icon: "shield",
    tone: "gold",
    locked: true,
    collapsed: true,
    columns: [
      {
        id: "pm-relief-work-image-1",
        title: "구호 물품 전달",
        description:
          "수해 피해 지역 주민들에게 긴급 구호 물품을 전달하는 현장입니다.",
        icon: "image",
        experienceType: "Image",
        experience: {
          title: "구호 물품 전달",
          description:
            "피해 지역에 필요한 지원을 전달합니다.",
          url: pmReliefImage1,
          urls: [
            pmReliefImage1,
          ],
        },
      },
      {
        id: "pm-relief-work-image-2",
        title: "복구와 재건",
        description:
          "홍수 이후 지역사회가 일상을 되찾고 재건해 가는 모습입니다.",
        icon: "image",
        experienceType: "Image",
        experience: {
          title: "복구와 재건",
          description:
            "피해 지역의 장기적인 복구를 지원합니다.",
          url: pmReliefImage2,
          urls: [
           pmReliefImage2,
          ],
        },
      },
      {
        id: "pm-relief-work-video-1",
        title: "네팔 구호 활동",
        description:
          "네팔에서 진행되는 긴급 구호와 복구 활동을 소개합니다.",
        icon: "youtube",
        experienceType: "Youtube",
        experience: {
          title: "네팔 구호 활동",
          description:
            "재난 피해 주민을 위한 현장 지원 영상입니다.",
          url: "https://www.youtube.com/watch?v=MhYVDPkFOD4",
          urls: [
            "https://www.youtube.com/watch?v=MhYVDPkFOD4",
          ],
        },
      },
      {
        id: "pm-relief-work-video-2",
        title: "홍수 대응 협력",
        description:
          "구조 활동과 정부 주도의 홍수 대응 과정을 보여 줍니다.",
        icon: "youtube",
        experienceType: "Youtube",
        experience: {
          title: "홍수 대응 협력",
          description:
            "피해 지역을 위한 구조 및 공동 대응 영상입니다.",
          url: "https://www.youtube.com/watch?v=hUQbst8FiHg",
          urls: [
            "https://www.youtube.com/watch?v=hUQbst8FiHg",
          ],
        },
      },
    ],
  },

  {
    id: "cube-aol",
    title: "아트 오브 리빙 네팔",
    description: "캠페인 기금 지원 기관",
    icon: "leaf",
    tone: "purple",
    locked: true,
    collapsed: true,
    columns: [
      {
        id: "aol-nepal-work-image-1",
        title: "지역사회 봉사",
        description:
          "아트 오브 리빙 네팔 자원봉사자들이 지역 주민을 지원하는 현장입니다.",
        icon: "image",
        experienceType: "Image",
        experience: {
          title: "아트 오브 리빙 네팔",
          description:
            "네팔 지역사회를 위한 봉사와 지원 활동입니다.",
          url: aolNepalImage1,
          urls: [aolNepalImage1],
        },
      },
      {
        id: "aol-nepal-work-image-2",
        title: "회복과 공동체 재건",
        description:
          "주민들의 치유와 지역사회의 회복을 돕는 활동입니다.",
        icon: "image",
        experienceType: "Image",
        experience: {
          title: "아트 오브 리빙 네팔",
          description:
            "더 건강하고 회복력 있는 공동체를 함께 만들어 갑니다.",
          url: aolNepalImage2,
          urls: [aolNepalImage2],
        },
      },
      {
        id: "aol-nepal-work-video-1",
        title: "네팔 전역에 전달된 구호",
        description:
          "홍수 피해 지역의 가족들에게 구호 물품을 전달하는 현장입니다.",
        icon: "youtube",
        experienceType: "Youtube",
        experience: {
          title: "네팔 지역사회 구호 활동",
          description:
            "자원봉사자들이 피해 주민들에게 필요한 지원을 전달합니다.",
          url: "https://www.youtube.com/shorts/9IP6sbSJwHY",
          urls: [
            "https://www.youtube.com/shorts/9IP6sbSJwHY",
          ],
        },
      },
      {
        id: "aol-nepal-work-video-2",
        title: "네팔을 향한 연대의 메시지",
        description:
          "홍수 피해를 입은 네팔 국민에게 전하는 위로와 연대의 메시지입니다.",
        icon: "youtube",
        experienceType: "Youtube",
        experience: {
          title: "네팔과 함께합니다",
          description:
            "피해 주민들의 회복을 기원하는 연대의 메시지입니다.",
          url: "https://www.youtube.com/shorts/9HuFV5CfSo4",
          urls: [
            "https://www.youtube.com/shorts/9HuFV5CfSo4",
          ],
        },
      },
    ],
  },

  {
    id: "cube-contribute",
    title: "기부 참여",
    description: "기부를 약속하고 참여 현황을 확인하세요",
    icon: "coins",
    tone: "green",
    locked: true,
    collapsed: true,
    columns: [],
  },

  {
    id: "cube-thanks",
    title: "감사의 마음",
    description: "Art of Living Nepal과 후원자가 전하는 감사의 이야기",
    icon: "heart",
    tone: "coral",
    locked: true,
    collapsed: false,
    columns: [
      {
        id: "thanks-aol-gratitude",
        title: "Art of Living Nepal 감사 인사",
        description: "네팔 구호 활동에 대한 감사의 마음",
        icon: "heart",
        experienceType: "Text",
        experience: {
          title: "Art of Living Nepal 감사 인사",
          description: "진심을 담아 감사드립니다.",
        },
      },
      {
        id: "president-thank-you-letter",
        title: "회장님께 드리는 감사의 편지",
        description: "경복동문회 회장님께 전하는 독립 감사 페이지",
        icon: "heart",
        experienceType: "Text",
        experience: {
          title: "회장님께 드리는 감사의 편지",
          description:
            "네팔을 위한 따뜻한 관심과 아낌없는 응원에 진심으로 감사드립니다.",
        },
      },
    ],
  },

  {
    id: "cube-contributors",
    title: "기부자 현황 관리",
    description: "기부 내역을 검토하고 입금 여부를 확인합니다",
    icon: "shield",
    tone: "gold",
    locked: true,
    collapsed: false,
    columns: [
      {
        id: "admin-funding-overview",
        title: "모금 현황",
        description: "캠페인 목표와 기부금 집계 현황",
        icon: "coins",
        experienceType: "Text",
        experience: {
          title: "모금 현황",
          description: "목표 달성률, 약속·인증 금액, 참여 건수를 확인합니다.",
        },
      },
    ],
  },

  {
    id: "cube-community",
    title: "현지 협력 파트너 초대",
    description: "개인 또는 단체를 현지 협력 파트너로 초대합니다",
    icon: "community",
    tone: "purple",
    locked: true,
    collapsed: true,
    columns: [],
  },
];

export const LEGACY_RECIPIENT_MEDIA_COLUMN_IDS = new Set([
  "pm-relief-work-images",
  "pm-relief-work-videos",
  "aol-nepal-work-images",
  "aol-nepal-work-videos",
]);

export function ensureDefaultImpactCubes(
  stored: AnchorCube[] | null | undefined
): AnchorCube[] {
  const incoming = (stored ?? []).map((cube) =>
    cube.id === "cube-updates" ? { ...cube, id: "cube-thanks" } : cube
  );
  const byId = new Map(incoming.map((cube) => [cube.id, cube]));
  const overview = byId.get("cube-overview");
  byId.delete("cube-overview");

  const lockedDefaults = DEFAULT_IMPACT_ANCHOR_CUBES.map((def) => {
    const existing = byId.get(def.id);
    if (!existing) {
      if (def.id === "cube-impact-card" && overview?.columns?.length) {
        return { ...def, columns: [...def.columns, ...overview.columns] };
      }
      return def;
    }

    byId.delete(def.id);
    const extraColumns =
      def.id === "cube-impact-card" && overview?.columns?.length
        ? overview.columns
        : [];
    const storedColumns = (existing.columns ?? []).filter(
      (column) => !LEGACY_RECIPIENT_MEDIA_COLUMN_IDS.has(column.id)
    );
    const storedById = new Map(storedColumns.map((column) => [column.id, column]));
    const defaultIds = new Set(def.columns.map((column) => column.id));

    return {
      ...def,
      ...existing,
      id: def.id,
      locked: def.locked,
      title: def.title,
      description: def.description,
      collapsed:
        def.id === "cube-thanks" || def.id === "cube-contributors"
          ? false
          : existing.collapsed,
      columns: [
        ...def.columns.map((defaultColumn) => {
          const storedColumn = storedById.get(defaultColumn.id);
          if (!storedColumn) return defaultColumn;
          return {
            ...defaultColumn,
            ...storedColumn,
            title: defaultColumn.title,
            description: defaultColumn.description,
            experience: defaultColumn.experience
            ? {
                ...(storedColumn.experience ?? {}),
                ...defaultColumn.experience,
              }
            : storedColumn.experience,
          };
        }),
        ...storedColumns.filter((column) => !defaultIds.has(column.id)),
        ...extraColumns,
      ],
    };
  });

  return [...lockedDefaults, ...byId.values()];
}

export function nextCubeTone(index: number): AnchorCubeTone {
  return ANCHOR_CUBE_TONES[index % ANCHOR_CUBE_TONES.length];
}

export function createAnchorCubeId(): string {
  return `cube-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
}

export function createAnchorColumnId(): string {
  return `col-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
}
